import { ChapletType } from '../types';

export interface BeadSequence {
  id: string;
  type: 'cross' | 'large-bead' | 'small-bead' | 'medal';
  prayers: string[]; // Which prayer step(s) use this bead
  position: number;
  label?: string; // Optional label for accessibility
}

export interface RosaryConfig {
  prayerType: ChapletType | 'daily-rosary';
  beadSequence: BeadSequence[];
  totalBeads: number;
  name: string;
}

// Standard Rosary Sequence (59 beads total) - Matches physical rosary structure
// Removed center-medal and decade-1-our-father to consolidate intro prayers on bead 5
export const DAILY_ROSARY_SEQUENCE: BeadSequence[] = [
  // Cross and introductory prayers
  { id: 'cross', type: 'cross', prayers: ['sign-of-cross', 'apostles-creed'], position: 0, label: 'Cross - Sign of the Cross, Apostles\' Creed' },

  // First large bead - Our Father
  { id: 'our-father-1', type: 'large-bead', prayers: ['our-father-1'], position: 1, label: 'Large Bead - Our Father' },

  // Three Hail Marys
  { id: 'hail-mary-1', type: 'small-bead', prayers: ['hail-mary-1'], position: 2, label: 'Small Bead - Hail Mary for Faith' },
  { id: 'hail-mary-2', type: 'small-bead', prayers: ['hail-mary-2'], position: 3, label: 'Small Bead - Hail Mary for Hope' },
  { id: 'hail-mary-3', type: 'small-bead', prayers: ['hail-mary-3'], position: 4, label: 'Small Bead - Hail Mary for Charity' },

  // Glory Be, Announce Mysteries, and 1st Decade Our Father - all on bead 5
  // This consolidates what was beads 5, 6, and 7 into a single bead
  { id: 'glory-be-intro', type: 'large-bead', prayers: ['glory-be-intro', 'fatima-prayer-intro', 'announce-mystery', 'decade-1-our-father'], position: 5, label: 'Large Bead - Glory Be, Fatima Prayer, Announce Mystery, Our Father (1st Mystery)' },

  // First Decade Hail Marys (now starting at position 6)
  ...Array.from({ length: 10 }, (_, i) => ({
    id: `decade-1-hail-mary-${i + 1}`,
    type: 'small-bead' as const,
    prayers: [`decade-1-hail-mary-${i + 1}`],
    position: 6 + i,
    label: `Small Bead - Hail Mary ${i + 1} (1st Mystery)`
  })),

  // Second Decade - Large bead handles Glory Be + Fatima from previous decade + Our Father for new decade
  { id: 'decade-2-our-father', type: 'large-bead', prayers: ['decade-1-glory-be', 'decade-1-fatima', 'decade-2-our-father'], position: 16, label: 'Large Bead - Glory Be, Fatima Prayer, Our Father (2nd Mystery)' },
  ...Array.from({ length: 10 }, (_, i) => ({
    id: `decade-2-hail-mary-${i + 1}`,
    type: 'small-bead' as const,
    prayers: [`decade-2-hail-mary-${i + 1}`],
    position: 17 + i,
    label: `Small Bead - Hail Mary ${i + 1} (2nd Mystery)`
  })),

  // Third Decade
  { id: 'decade-3-our-father', type: 'large-bead', prayers: ['decade-2-glory-be', 'decade-2-fatima', 'decade-3-our-father'], position: 27, label: 'Large Bead - Glory Be, Fatima Prayer, Our Father (3rd Mystery)' },
  ...Array.from({ length: 10 }, (_, i) => ({
    id: `decade-3-hail-mary-${i + 1}`,
    type: 'small-bead' as const,
    prayers: [`decade-3-hail-mary-${i + 1}`],
    position: 28 + i,
    label: `Small Bead - Hail Mary ${i + 1} (3rd Mystery)`
  })),

  // Fourth Decade
  { id: 'decade-4-our-father', type: 'large-bead', prayers: ['decade-3-glory-be', 'decade-3-fatima', 'decade-4-our-father'], position: 38, label: 'Large Bead - Glory Be, Fatima Prayer, Our Father (4th Mystery)' },
  ...Array.from({ length: 10 }, (_, i) => ({
    id: `decade-4-hail-mary-${i + 1}`,
    type: 'small-bead' as const,
    prayers: [`decade-4-hail-mary-${i + 1}`],
    position: 39 + i,
    label: `Small Bead - Hail Mary ${i + 1} (4th Mystery)`
  })),

  // Fifth Decade
  { id: 'decade-5-our-father', type: 'large-bead', prayers: ['decade-4-glory-be', 'decade-4-fatima', 'decade-5-our-father'], position: 49, label: 'Large Bead - Glory Be, Fatima Prayer, Our Father (5th Mystery)' },
  ...Array.from({ length: 10 }, (_, i) => ({
    id: `decade-5-hail-mary-${i + 1}`,
    type: 'small-bead' as const,
    prayers: [`decade-5-hail-mary-${i + 1}`],
    position: 50 + i,
    label: `Small Bead - Hail Mary ${i + 1} (5th Mystery)`
  })),

  // Final prayers after last decade
  { id: 'final-prayers', type: 'medal', prayers: ['decade-5-glory-be', 'decade-5-fatima', 'hail-holy-queen', 'closing-prayer'], position: 60, label: 'Medal - Glory Be, Fatima Prayer, Hail Holy Queen, Closing Prayer' }
];

// Divine Mercy Chaplet (58 beads)
// Unlike the rosary, the Divine Mercy opening prayers (Our Father, Hail Mary,
// Apostles' Creed) are all said on the single large bead before the first
// decade — so the 3 intro Hail Mary beads and second large bead that a rosary
// carries are omitted from this sequence.
export const DIVINE_MERCY_SEQUENCE: BeadSequence[] = [
  { id: 'cross', type: 'cross', prayers: ['sign-of-cross', 'opening-prayer'], position: 0, label: 'Cross - Sign of the Cross, Opening Prayer' },
  { id: 'our-father-1', type: 'large-bead', prayers: ['our-father', 'hail-mary', 'apostles-creed'], position: 1, label: 'Large Bead - Our Father, Hail Mary, Apostles\' Creed' },

  // Five decades of Divine Mercy prayers
  ...Array.from({ length: 5 }, (_, decade) => {
    const decadeStart = 2 + (decade * 11);
    return [
      {
        id: `decade-${decade + 1}-eternal-father`,
        type: 'large-bead' as const,
        prayers: [`decade-${decade + 1}-eternal-father`],
        position: decadeStart,
        label: `Large Bead - Eternal Father (${decade + 1})`
      },
      ...Array.from({ length: 10 }, (_, i) => ({
        id: `decade-${decade + 1}-mercy-${i + 1}`,
        type: 'small-bead' as const,
        prayers: [`decade-${decade + 1}-mercy-${i + 1}`],
        position: decadeStart + 1 + i,
        label: `Small Bead - For the sake of His sorrowful Passion... (${decade + 1})`
      }))
    ];
  }).flat(),

  { id: 'closing-trisagion', type: 'medal', prayers: ['trisagion-1', 'trisagion-2', 'trisagion-3'], position: 57, label: 'Medal - Holy God (3x)' }
];

// Seven Sorrows Chaplet (52 beads)
export const SEVEN_SORROWS_SEQUENCE: BeadSequence[] = [
  { id: 'cross', type: 'cross', prayers: ['sign-of-cross', 'act-of-contrition'], position: 0, label: 'Cross - Sign of Cross, Act of Contrition' },
  
  // Seven groups of 7 Hail Marys each
  ...Array.from({ length: 7 }, (_, sorrow) => {
    const sorrowStart = 1 + (sorrow * 7);
    return [
      ...Array.from({ length: 7 }, (_, i) => ({
        id: `sorrow-${sorrow + 1}-hail-mary-${i + 1}`,
        type: 'small-bead' as const,
        prayers: [`sorrow-${sorrow + 1}-hail-mary-${i + 1}`],
        position: sorrowStart + i,
        label: `Small Bead - Hail Mary ${i + 1} (${sorrow + 1}${sorrow === 0 ? 'st' : sorrow === 1 ? 'nd' : sorrow === 2 ? 'rd' : 'th'} Sorrow)`
      }))
    ];
  }).flat(),
  
  { id: 'closing-prayer', type: 'medal', prayers: ['closing-prayer'], position: 50, label: 'Medal - Closing Prayer' }
];

// St. Michael Chaplet (39 beads)
export const ST_MICHAEL_SEQUENCE: BeadSequence[] = [
  { id: 'cross', type: 'cross', prayers: ['sign-of-cross', 'opening-prayer'], position: 0, label: 'Cross - Sign of Cross, Opening Prayer' },
  
  // Nine angelic choirs (4 beads each: 1 Our Father + 3 Hail Marys)
  ...Array.from({ length: 9 }, (_, choir) => {
    const choirStart = 1 + (choir * 4);
    const choirNames = ['Seraphim', 'Cherubim', 'Thrones', 'Dominions', 'Powers', 'Virtues', 'Principalities', 'Archangels', 'Angels'];
    return [
      {
        id: `choir-${choir + 1}-our-father`,
        type: 'large-bead' as const,
        prayers: [`choir-${choir + 1}-our-father`],
        position: choirStart,
        label: `Large Bead - Our Father (${choirNames[choir]})`
      },
      ...Array.from({ length: 3 }, (_, i) => ({
        id: `choir-${choir + 1}-hail-mary-${i + 1}`,
        type: 'small-bead' as const,
        prayers: [`choir-${choir + 1}-hail-mary-${i + 1}`],
        position: choirStart + 1 + i,
        label: `Small Bead - Hail Mary ${i + 1} (${choirNames[choir]})`
      }))
    ];
  }).flat(),
  
  { id: 'st-michael-prayer', type: 'medal', prayers: ['st-michael-prayer'], position: 37, label: 'Medal - St. Michael Prayer' }
];

// Rosary configurations for each prayer type
export const ROSARY_CONFIGS: Record<ChapletType | 'daily-rosary', RosaryConfig> = {
  'daily-rosary': {
    prayerType: 'daily-rosary',
    beadSequence: DAILY_ROSARY_SEQUENCE,
    totalBeads: 61, // Reduced from 63 after consolidating beads 5-7 into bead 5
    name: 'Daily Rosary'
  },
  'divine-mercy': {
    prayerType: 'divine-mercy',
    beadSequence: DIVINE_MERCY_SEQUENCE,
    totalBeads: 58,
    name: 'Divine Mercy Chaplet'
  },
  'seven-sorrows': {
    prayerType: 'seven-sorrows',
    beadSequence: SEVEN_SORROWS_SEQUENCE,
    totalBeads: 51,
    name: 'Seven Sorrows Chaplet'
  },
  'st-michael': {
    prayerType: 'st-michael',
    beadSequence: ST_MICHAEL_SEQUENCE,
    totalBeads: 38,
    name: 'St. Michael Chaplet'
  },
  'sacred-heart': {
    prayerType: 'sacred-heart',
    beadSequence: [
      { id: 'cross', type: 'cross', prayers: ['sign-of-cross'], position: 0, label: 'Cross - Sign of Cross' },
      ...Array.from({ length: 33 }, (_, i) => ({
        id: `prayer-${i + 1}`,
        type: i % 3 === 0 ? 'large-bead' as const : 'small-bead' as const,
        prayers: [`prayer-${i + 1}`],
        position: i + 1,
        label: `${i % 3 === 0 ? 'Large' : 'Small'} Bead - Prayer ${i + 1}`
      }))
    ],
    totalBeads: 34,
    name: 'Sacred Heart Chaplet'
  },
  'precious-blood': {
    prayerType: 'precious-blood',
    beadSequence: [
      { id: 'cross', type: 'cross', prayers: ['sign-of-cross'], position: 0, label: 'Cross - Sign of Cross' },
      ...Array.from({ length: 33 }, (_, i) => ({
        id: `prayer-${i + 1}`,
        type: i % 7 === 0 ? 'large-bead' as const : 'small-bead' as const,
        prayers: [`prayer-${i + 1}`],
        position: i + 1,
        label: `${i % 7 === 0 ? 'Large' : 'Small'} Bead - Prayer ${i + 1}`
      }))
    ],
    totalBeads: 34,
    name: 'Precious Blood Chaplet'
  },
  'holy-face': {
    prayerType: 'holy-face',
    beadSequence: [
      { id: 'cross', type: 'cross', prayers: ['sign-of-cross'], position: 0, label: 'Cross - Sign of Cross' },
      ...Array.from({ length: 33 }, (_, i) => ({
        id: `prayer-${i + 1}`,
        type: 'small-bead' as const,
        prayers: [`prayer-${i + 1}`],
        position: i + 1,
        label: `Small Bead - Prayer ${i + 1}`
      }))
    ],
    totalBeads: 34,
    name: 'Holy Face Chaplet'
  },
  'immaculate-heart': {
    prayerType: 'immaculate-heart',
    beadSequence: [
      { id: 'cross', type: 'cross', prayers: ['sign-of-cross'], position: 0, label: 'Cross - Sign of Cross' },
      ...Array.from({ length: 54 }, (_, i) => ({
        id: `prayer-${i + 1}`,
        type: i % 10 === 0 ? 'large-bead' as const : 'small-bead' as const,
        prayers: [`prayer-${i + 1}`],
        position: i + 1,
        label: `${i % 10 === 0 ? 'Large' : 'Small'} Bead - Prayer ${i + 1}`
      }))
    ],
    totalBeads: 55,
    name: 'Immaculate Heart Chaplet'
  },
  'st-joseph': {
    prayerType: 'st-joseph',
    beadSequence: [
      { id: 'cross', type: 'cross', prayers: ['sign-of-cross'], position: 0, label: 'Cross - Sign of Cross' },
      ...Array.from({ length: 30 }, (_, i) => ({
        id: `prayer-${i + 1}`,
        type: i % 6 === 0 ? 'large-bead' as const : 'small-bead' as const,
        prayers: [`prayer-${i + 1}`],
        position: i + 1,
        label: `${i % 6 === 0 ? 'Large' : 'Small'} Bead - Prayer ${i + 1}`
      }))
    ],
    totalBeads: 31,
    name: 'St. Joseph Chaplet'
  },
  'five-wounds': {
    prayerType: 'five-wounds',
    beadSequence: [
      { id: 'cross', type: 'cross', prayers: ['sign-of-cross'], position: 0, label: 'Cross - Sign of Cross' },
      ...Array.from({ length: 33 }, (_, i) => ({
        id: `prayer-${i + 1}`,
        type: i % 5 === 0 ? 'large-bead' as const : 'small-bead' as const,
        prayers: [`prayer-${i + 1}`],
        position: i + 1,
        label: `${i % 5 === 0 ? 'Large' : 'Small'} Bead - Prayer ${i + 1}`
      }))
    ],
    totalBeads: 34,
    name: 'Five Wounds Chaplet'
  },
  'st-bridget': {
    prayerType: 'st-bridget',
    beadSequence: [
      { id: 'cross', type: 'cross', prayers: ['sign-of-cross'], position: 0, label: 'Cross - Sign of Cross' },
      ...Array.from({ length: 63 }, (_, i) => ({
        id: `prayer-${i + 1}`,
        type: i < 7 || (i >= 14 && i < 21) ? 'large-bead' as const : 'small-bead' as const,
        prayers: [`prayer-${i + 1}`],
        position: i + 1,
        label: `${i < 7 || (i >= 14 && i < 21) ? 'Large' : 'Small'} Bead - Prayer ${i + 1}`
      }))
    ],
    totalBeads: 64,
    name: 'St. Bridget Chaplet'
  }
};

// Helper function to get rosary config by prayer type
export const getRosaryConfig = (prayerType: ChapletType | 'daily-rosary' | '54-day-novena' | 'chaplet' | string): RosaryConfig => {
  // Map prayer types to rosary configs
  if (prayerType === '54-day-novena' || prayerType === 'daily-rosary') {
    return ROSARY_CONFIGS['daily-rosary'];
  }
  
  // For chaplet type, use the default divine mercy sequence
  if (prayerType === 'chaplet') {
    return ROSARY_CONFIGS['divine-mercy'];
  }
  
  // For specific chaplet types
  if (prayerType in ROSARY_CONFIGS) {
    return ROSARY_CONFIGS[prayerType as keyof typeof ROSARY_CONFIGS];
  }
  
  // Default fallback
  return ROSARY_CONFIGS['daily-rosary'];
};